export interface MultiSelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface MultiSelectGroup<
  O extends MultiSelectOption = MultiSelectOption,
> {
  label: string
  options: O[]
}

export type MultiSelectEntry<O extends MultiSelectOption = MultiSelectOption> =
  | O
  | MultiSelectGroup<O>

// What Base UI's `items` takes for a grouped list: a `label`-less group is a
// run of loose options sitting between headed groups.
export interface MultiSelectListGroup {
  label: string | null
  items: string[]
}

const isGroup = <O extends MultiSelectOption>(
  entry: MultiSelectEntry<O>,
): entry is MultiSelectGroup<O> => 'options' in entry

export const flattenOptions = <O extends MultiSelectOption>(
  entries: Array<MultiSelectEntry<O>>,
): O[] => entries.flatMap((entry) => (isGroup(entry) ? entry.options : entry))

// The `items` for Base UI's Combobox: plain values when the list is flat,
// groups when any entry is a group. `createValue`, when set, is the "Create x"
// row, appended last so that Enter on a query with a real match picks the
// match, and Enter on a query with none creates.
export const toListItems = <O extends MultiSelectOption>(
  entries: Array<MultiSelectEntry<O>>,
  createValue: string | null,
): string[] | MultiSelectListGroup[] => {
  if (!entries.some(isGroup)) {
    const values = flattenOptions(entries).map((option) => option.value)
    return createValue === null ? values : [...values, createValue]
  }

  const groups: MultiSelectListGroup[] = []
  for (const entry of entries) {
    if (isGroup(entry)) {
      groups.push({
        label: entry.label,
        items: entry.options.map((option) => option.value),
      })
      continue
    }
    const last = groups.at(-1)
    if (last && last.label === null) last.items.push(entry.value)
    else groups.push({ label: null, items: [entry.value] })
  }
  if (createValue !== null) groups.push({ label: null, items: [createValue] })
  return groups
}

export const defaultFilter = (
  query: string,
  option: MultiSelectOption,
): boolean => option.label.toLowerCase().includes(query.trim().toLowerCase())

// The text the "Create x" row would add, or null when there is nothing to
// create: an empty query, or one that already names an option or a chip.
export const creatableValue = (
  query: string,
  options: MultiSelectOption[],
  values: string[],
): string | null => {
  const text = query.trim()
  if (text === '') return null
  const lower = text.toLowerCase()
  const taken =
    options.some(
      (option) =>
        option.value.toLowerCase() === lower ||
        option.label.toLowerCase() === lower,
    ) || values.some((value) => value.toLowerCase() === lower)
  return taken ? null : text
}

// Splits typed or pasted text on the separators. `rest` is whatever follows the
// last separator and stays in the input; null means no separator was typed.
export const splitOnSeparators = (
  raw: string,
  separators: string[],
): { tokens: string[]; rest: string } | null => {
  if (!separators.some((separator) => raw.includes(separator))) return null
  const segments = separators.reduce(splitEach, [raw])
  const rest = segments.pop() ?? ''
  const tokens = segments
    .map((segment) => segment.trim())
    .filter((segment) => segment !== '')
  return { tokens, rest }
}

const splitEach = (parts: string[], separator: string): string[] =>
  parts.flatMap((part) => part.split(separator))

// A token typed in full names an option by its value or label; anything else is
// free text and becomes its own value.
export const resolveToken = (
  token: string,
  options: MultiSelectOption[],
): string => {
  const lower = token.toLowerCase()
  const match = options.find(
    (option) =>
      option.value.toLowerCase() === lower ||
      option.label.toLowerCase() === lower,
  )
  return match ? match.value : token
}

export const appendUnique = <V>(values: V[], added: V[]): V[] => {
  const next = [...values]
  for (const value of added) if (!next.includes(value)) next.push(value)
  return next
}
